/**
 * Decision 0142 (messaging phase 1): `SendMessage`, `MarkMessagesRead`, `RecordMessageDelivery`.
 *
 * One message model on the comment and wake pipeline (Proposal B of the messaging investigation): a message is a
 * `CommentPosted` in a thread (`T-n`), followed by one `MessageDelivered` per recipient, so who was woken, steered or
 * told is in the log. No limits: no message budget and no loop cap (0140 #24, #27); every delivery is recorded so loops
 * are visible.
 *
 * - Kind: an agent's or a worker's message that expects a reply from exactly one agent is an `ask` (it wakes as `ask`,
 *   is answered, and is a loose end after a day). An agent's message in a thread where an ask to it is open is the
 *   `answer`. Everything else is a `message`: an agent wakes as `agent_mention` (a person's as `human_mention`).
 * - Routes: agents `wake`; live workers `steer` (else `next_attempt`); people `notice`; a person's external agent
 *   `event`; a person's coding session `session`. External participants keep the message pending until they pick it
 *   up (`MarkMessagesRead`).
 * - Who may write to whom: anyone in the project to anyone in it. An external participant must belong to a member
 *   (private projects never reach one whose owner isn't in them). A person writes through one of their own participants
 *   only (`via`).
 */

import { noteTrustOf } from "../multiplayer"
import { agentOf, memberOf, recordOf, runOf } from "../state"
import {
  isLiveRun,
  messageExcerpt,
  nextThreadRef,
  partyKey,
  partyOfActor,
  senderAgentOf,
  threadFor,
  threadOf,
  uniqueParties,
} from "../threads"
import type { Command, EntityRef, Ids, MessageParty, ProjectState, Thread } from "../types"
import { actorTaint, forbidden, invalid, notFound, type Tx } from "./tx"

type Cmd<T extends Command["_tag"]> = Extract<Command, { _tag: T }>

/** Check one recipient; returns the problem, or null. */
const recipientProblem = (s: ProjectState, p: MessageParty): string | null => {
  switch (p._tag) {
    case "agent": {
      const a = agentOf(s, p.agentId)
      return !a || a.status === "retired" ? "no such agent in this project" : null
    }
    case "user":
      return memberOf(s, p.userId) ? null : "not a member of this project"
    case "worker": {
      const r = runOf(s, p.runId)
      return r && (r.kind === "worker" || r.kind === "validator")
        ? null
        : "no such worker run in this project"
    }
    case "external":
      // 0126 §3 / 0129: a person's participant sees what they see; private projects never reach a non-member's
      return memberOf(s, p.ownerId) ? null : "its owner is not a member of this project"
  }
}

const routeOf = (s: ProjectState, p: MessageParty) => {
  switch (p._tag) {
    case "agent":
      return "wake" as const
    case "worker":
      return isLiveRun(s, p.runId) ? ("steer" as const) : ("next_attempt" as const)
    case "user":
      return "notice" as const
    case "external":
      return p.kind === "agent" ? ("event" as const) : ("session" as const)
  }
}

export const sendMessage = (tx: Tx, c: Cmd<"SendMessage">): void => {
  const a = tx.actor
  if (a._tag !== "user" && a._tag !== "agent" && a._tag !== "worker")
    forbidden("actor_kind", "agents, workers and people send messages")
  // a person writes through their own participant only (the tool layer resolved it from their OAuth client)
  const via = c.via ?? null
  if (via && (a._tag !== "user" || via._tag !== "external" || via.ownerId !== a.userId))
    forbidden("not_owner", "a message goes through your own participant only")
  const sender = partyOfActor(a, via)
  if (!sender) forbidden("actor_kind", "agents, workers and people send messages")
  const s = tx.state
  const body = c.body.trim()
  if (body === "") invalid("say something", "body")

  // the thread: continue the one named, or the one on this record between exactly these parties, or open one
  let thread: Thread | undefined
  let on: EntityRef | null = c.on
  if (c.thread !== null) {
    thread = threadOf(s, c.thread)
    if (!thread) notFound({ _tag: "thread", id: c.thread })
    on = thread.on
  } else if (on !== null) {
    if (on._tag === "thread") invalid("continue a thread with `thread`, not `on`", "on")
    if (!recordOf(s, on)) notFound(on)
  }
  const senderKey = partyKey(sender)
  // a person never writes to their own participants (they are the person), nor anyone to themselves
  const notSelf = (p: MessageParty) =>
    partyKey(p) !== senderKey &&
    !(a._tag === "user" && p._tag === "external" && p.ownerId === a.userId) &&
    !(a._tag === "user" && p._tag === "user" && p.userId === a.userId)
  const named = uniqueParties(c.to)
  if (named.some((p) => !notSelf(p))) invalid("you can't message yourself", "to")
  const to = uniqueParties(named.length > 0 ? named : (thread?.parties ?? []).filter(notSelf))
  if (to.length === 0)
    invalid(thread ? "nobody else is in this thread: name `to`" : "name whom you write to", "to")
  to.forEach((p, i) => {
    const problem = recipientProblem(s, p)
    if (problem) invalid(problem, `to[${i}]`)
  })
  if (!thread && named.length > 0) thread = threadFor(s, on, uniqueParties([sender, ...to]))

  // what it is: an ask to one agent, the answer to an open ask to the sender in this thread, or a message
  const agents = to.filter((p) => p._tag === "agent")
  const asksOne =
    c.expectsReply === true &&
    (a._tag === "agent" || a._tag === "worker") &&
    to.length === 1 &&
    agents.length === 1
  const current = thread
  const openAsk =
    a._tag === "agent" && current
      ? s.openAsks.find((x) => x.threadId === current.id && x.ask?.state === "open" && x.ask.to === a.agentId)
      : undefined
  const kind: "ask" | "answer" | "message" = asksOne ? "ask" : openAsk ? "answer" : "message"
  const tainted = actorTaint(s, a) === "tainted"
  const askerAgent = openAsk ? senderAgentOf(openAsk.createdBy) : null

  if (!thread) {
    const ref = nextThreadRef(s)
    tx.emit({
      _tag: "ThreadOpened",
      thread: {
        id: c.commentId,
        ref,
        on,
        parties: uniqueParties([sender, ...to]),
        createdBy: a,
        createdAt: tx.now,
        lastAt: tx.now,
        lastSeq: (s.seq + 1) as Ids["Seq"],
        messages: 0,
        pending: [],
      },
    })
    thread = threadOf(tx.state, c.commentId) as Thread
  }
  const threadId = thread.id
  const target: EntityRef = on ?? { _tag: "thread", id: threadId }
  // wakes: every agent recipient (an answer's asker is woken by `answer`, never twice)
  const wakes = agents.flatMap((p) => {
    if (p._tag !== "agent" || p.agentId === askerAgent) return []
    const agent = agentOf(tx.state, p.agentId)
    if (!agent || (agent.status !== "active" && agent.status !== "paused")) return []
    return [
      {
        agentId: p.agentId,
        untrusted: a._tag === "user" ? !noteTrustOf(tx.state, agent.ownerId)(a.userId) : tainted,
      },
    ]
  })
  const ask0 = asksOne ? agents[0] : undefined
  tx.emit({
    _tag: "CommentPosted",
    comment: {
      ...tx.recordMeta(),
      contentSeq: (tx.state.seq + 1) as Ids["Seq"],
      id: c.commentId,
      target,
      threadId,
      kind,
      body,
      mentions: [],
      ask: ask0?._tag === "agent" ? { to: ask0.agentId, state: "open" } : null,
      answers: openAsk ? openAsk.id : null,
      wakes,
      tainted,
      editedAt: null,
      resolvedAt: null,
      to,
      ...(via?._tag === "external" ? { via: via.handle } : {}),
      ...(c.expectsReply === true ? { expectsReply: true } : {}),
    },
  })
  if (openAsk) tx.emit({ _tag: "AskStateChanged", askId: openAsk.id, state: "answered", by: c.commentId })
  // one delivery record per recipient (decision 0142: every delivery is in the log)
  for (const p of to) {
    const route = routeOf(tx.state, p)
    const outside = route === "event" || route === "session" || route === "notice"
    tx.emit({
      _tag: "MessageDelivered",
      commentId: c.commentId,
      threadId,
      from: sender,
      to: p,
      route,
      state: "queued",
      ...(outside ? { excerpt: messageExcerpt(body) } : {}),
      ...(tainted ? { tainted: true } : {}),
      ...(c.expectsReply === true ? { expectsReply: true } : {}),
    })
  }
}

/** A person's external participant picked these messages up. Idempotent: only pending ones are recorded. */
export const markMessagesRead = (tx: Tx, c: Cmd<"MarkMessagesRead">): void => {
  const a = tx.actor
  if (a._tag !== "user") forbidden("actor_kind", "a person's client marks its messages read")
  const want = new Set<string>(c.commentIds)
  for (const t of tx.state.threads ?? []) {
    for (const p of t.pending) {
      if (p.participantId !== c.participantId || p.ownerId !== a.userId || !want.has(p.commentId)) continue
      const party = t.parties.find((x) => x._tag === "external" && x.participantId === p.participantId)
      if (!party) continue
      tx.emit({
        _tag: "MessageDelivered",
        commentId: p.commentId,
        threadId: t.id,
        from: null,
        to: party,
        route: p.kind === "agent" ? "event" : "session",
        state: "read",
      })
    }
  }
}

/** system:gateway: the lead's chat reply to a person's ask_lead, pushed to the client that asked. */
export const recordMessageDelivery = (tx: Tx, c: Cmd<"RecordMessageDelivery">): void => {
  const a = tx.actor
  if (a._tag !== "system" || a.process !== "gateway")
    forbidden("actor_kind", "only the gateway records a delivery outside a message")
  const problem = recipientProblem(tx.state, c.to)
  if (problem) invalid(problem, "to")
  if (c.to._tag !== "user" && c.to._tag !== "external") invalid("a pushed reply goes to a person", "to")
  if (!agentOf(tx.state, c.chat.agentId)) invalid("no such agent in this project", "chat.agentId")
  tx.emit({
    _tag: "MessageDelivered",
    commentId: null,
    threadId: null,
    chat: c.chat,
    from: { _tag: "agent", agentId: c.chat.agentId },
    to: c.to,
    route: c.route,
    state: "queued",
    excerpt: messageExcerpt(c.excerpt),
  })
}
